// SPDX-License-Identifier: AGPL-3.0-or-later

import {serverNow} from '@app/features/voice_scene/net/RoomServerClock';
import {type VoiceWatch, voiceWatchPositionMs} from '@fluxer/constants/src/VoiceRoomWatch';

// The room TV's YouTube player, driven over the embed's postMessage protocol: YouTube's iframe_api script is never
// loaded. It runs on YouTube's privacy-enhanced embed host; commands go only to that origin and only its messages
// from our own frame are read.
export const YOUTUBE_ORIGIN = 'https://www.youtube-nocookie.com';
// YouTube's smallest allowed player; under it (or out of view) the TV pauses, mutes and shows the thumbnail.
export const MIN_PLAYER_EDGE_PX = 200;
// Viewers this close to the shared time are left alone, so a pause or a small skip does not jolt everyone.
const DRIFT_TOLERANCE_S = 2;
const LISTEN_INTERVAL_MS = 250;
const LISTEN_TRIES = 40;
const PLAYING = 1;

// videoId: 11 characters of [A-Za-z0-9_-], as decodeVoiceWatch only lets through.
export function youTubeEmbedUrl(videoId: string, origin: string): string {
	const params = new URLSearchParams({enablejsapi: '1', controls: '0', disablekb: '1', playsinline: '1', origin});
	return `${YOUTUBE_ORIGIN}/embed/${videoId}?${params}`;
}

export function youTubeThumbnailUrl(videoId: string): string {
	return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}

export interface TvVideoState {
	watch: VoiceWatch;
	// The TV shows in its cone and big enough for YouTube's player.
	playable: boolean;
	// This viewer's volume, 0 to 100; 0 is silent.
	volume: number;
	// false while this viewer is deafened.
	audible: boolean;
	// This viewer's subtitles; off, YouTube's own choice (it turns them on by language) is overruled.
	captions: boolean;
}

export interface YouTubeTvPlayer {
	// The frame's document loaded: ask the player for its events now rather than at the next try.
	frameLoaded(): void;
	receive(event: MessageEvent): void;
	set(state: TvVideoState): void;
	dispose(): void;
}

// target: the embed frame's window. Until the player says it is ready, the wanted state is only kept.
export function createYouTubeTvPlayer({
	target,
	now = serverNow,
}: {
	target: () => Window | null;
	now?: () => number;
}): YouTubeTvPlayer {
	let ready = false;
	let wanted: TvVideoState | null = null;
	let sentSilent: boolean | null = null;
	let sentVolume: number | null = null;
	let sentCaptions: boolean | null = null;
	let synced: {startedAtMs: number; pausedAtMs: number | null; playing: boolean} | null = null;
	let reported: {seconds: number; atMs: number; playing: boolean} | null = null;
	let tries = 0;
	let listening: ReturnType<typeof setInterval> | null = null;

	const post = (message: Record<string, unknown>): void => {
		target()?.postMessage(JSON.stringify({...message, id: 1, channel: 'widget'}), YOUTUBE_ORIGIN);
	};
	const command = (func: string, args: Array<unknown> = []): void => post({event: 'command', func, args});
	const stopListening = (): void => {
		if (listening !== null) clearInterval(listening);
		listening = null;
	};
	const listen = (): void => {
		post({event: 'listening'});
		tries += 1;
		if (tries >= LISTEN_TRIES) stopListening();
	};
	// The player answers only once its script runs, some time after the frame loads: asked a few times a second for
	// a while, from now and again from the load.
	const startListening = (): void => {
		if (ready) return;
		stopListening();
		tries = 0;
		listen();
		listening = setInterval(listen, LISTEN_INTERVAL_MS);
	};
	startListening();

	const reportedSeconds = (): number | null =>
		reported && reported.seconds + (reported.playing ? (now() - reported.atMs) / 1000 : 0);

	// The player loads (or not) its captions module by its own choice when a video starts playing: this viewer's choice is
	// sent again then.
	const sendCaptions = (on: boolean): void => {
		command(on ? 'loadModule' : 'unloadModule', ['captions']);
		sentCaptions = on;
	};

	const apply = (): void => {
		if (!ready || !wanted) return;
		const {watch, playable, volume, audible, captions} = wanted;
		if (captions !== sentCaptions) sendCaptions(captions);
		const silent = !playable || !audible || volume <= 0;
		if (silent !== sentSilent) {
			command(silent ? 'mute' : 'unMute');
			sentSilent = silent;
		}
		if (!silent && volume !== sentVolume) {
			command('setVolume', [volume]);
			sentVolume = volume;
		}
		const playing = playable && watch.pausedAtMs === null;
		if (
			synced?.startedAtMs === watch.startedAtMs &&
			synced.pausedAtMs === watch.pausedAtMs &&
			synced.playing === playing
		) {
			return;
		}
		synced = {startedAtMs: watch.startedAtMs, pausedAtMs: watch.pausedAtMs, playing};
		if (playable) {
			const shared = voiceWatchPositionMs(watch, now()) / 1000;
			const at = reportedSeconds();
			if (at === null || Math.abs(at - shared) > DRIFT_TOLERANCE_S) command('seekTo', [shared, true]);
		}
		command(playing ? 'playVideo' : 'pauseVideo');
	};

	return {
		frameLoaded: startListening,
		receive(event) {
			if (event.origin !== YOUTUBE_ORIGIN || event.source !== target() || typeof event.data !== 'string') return;
			let data: unknown;
			try {
				data = JSON.parse(event.data);
			} catch {
				return;
			}
			if (typeof data !== 'object' || data === null) return;
			const {event: name, info} = data as {event?: unknown; info?: unknown};
			if (name === 'onReady') {
				ready = true;
				stopListening();
				apply();
				return;
			}
			if (typeof info !== 'object' || info === null) return;
			const {currentTime, playerState} = info as {currentTime?: unknown; playerState?: unknown};
			if (typeof currentTime !== 'number') return;
			const wasPlaying = reported?.playing ?? false;
			reported = {
				seconds: currentTime,
				atMs: now(),
				playing: typeof playerState === 'number' ? playerState === PLAYING : wasPlaying,
			};
			if (reported.playing && !wasPlaying && wanted) sendCaptions(wanted.captions);
		},
		set(state) {
			wanted = state;
			apply();
		},
		dispose() {
			stopListening();
			ready = false;
			wanted = null;
		},
	};
}
